import logo from '../assets/logo_new.png'
import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation, Link } from "react-router-dom";
import { useLanguage, getAltLangPath } from '../i18n/LanguageContext';
import { headerContent } from '../i18n/content/header';

const MAIL_ICON = (
    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="3"></rect>
        <path d="m3 7 9 6 9-6"></path>
    </svg>
);

const PHONE_ICON = (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>
);

const PHONE_NUMBER = "+32 460 20 15 77";
const PHONE_TEL = "tel:+32460201577";

export default function Header() {
    const [isHidden, setIsHidden] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const lastScrollYRef = useRef(0);
    const tickingRef = useRef(false);
    const { lang, withPath } = useLanguage();
    const location = useLocation();
    const c = headerContent[lang];
    const otherLang = lang === 'es' ? 'fr' : 'es';
    const altLangPath = getAltLangPath(location.pathname, otherLang);

    useEffect(() => {
        lastScrollYRef.current = window.scrollY || 0;

        const updateOnScroll = () => {
            const currentY = window.scrollY || 0;
            const header = document.querySelector('header');

            // Toggle scrolled class for styling effects when past threshold
            if (header) {
                if (currentY > 100) {
                    header.classList.add('scrolled');
                } else {
                    header.classList.remove('scrolled');
                }
            }

            const delta = currentY - lastScrollYRef.current;
            const threshold = 5; // prevent jitter on tiny scrolls

            if (Math.abs(delta) > threshold) {
                const isScrollingDown = delta > 0;
                if (isScrollingDown && currentY > 80) {
                    setIsHidden(true);
                } else {
                    setIsHidden(false);
                }
                lastScrollYRef.current = currentY;
            }
        };

        const onScroll = () => {
            if (!tickingRef.current) {
                window.requestAnimationFrame(() => {
                    updateOnScroll();
                    tickingRef.current = false;
                });
                tickingRef.current = true;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Mobile menu: ESC to close + lock body scroll while open
    useEffect(() => {
        if (!menuOpen) return;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [menuOpen]);

    return (
        <>
            <header className={isHidden && !menuOpen ? 'header-hidden' : 'header-visible'}>
                <div className='header-content flex items-center justify-between p-2 sm:p-3 mt-2 sm:mt-3 w-[92%] sm:w-[85%] m-auto'>
                    <NavLink to={withPath('/')} className='flex items-center gap-2' onClick={() => setMenuOpen(false)}>
                        <img src={logo} alt="logo" loading='lazy' className='logo w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12' />
                    </NavLink>

                    {/* Desktop nav */}
                    <nav className='hidden lg:flex items-center gap-8'>
                        {c.navLinks.map((link) => (
                            <a
                                key={link.href}
                                href={link.href}
                                className='text-sm font-medium text-ink hover:text-primary transition-colors duration-300'
                            >
                                {link.label}
                            </a>
                        ))}
                    </nav>

                    {/* Desktop Contact & Button */}
                    <div className='hidden lg:flex items-center justify-end gap-3'>
                        <Link
                            to={altLangPath}
                            aria-label={otherLang === 'es' ? 'Cambiar a español' : 'Passer en français'}
                            className='flex items-center justify-center w-10 h-10 rounded-full border border-line text-ink-muted text-xs font-bold uppercase transition-all duration-300 hover:border-primary hover:text-primary'
                        >
                            {otherLang}
                        </Link>
                        <a
                            href={PHONE_TEL}
                            aria-label={c.callLabel(PHONE_NUMBER)}
                            className='flex items-center gap-2 px-3 h-10 rounded-full border border-line text-ink-muted transition-all duration-300 hover:border-primary hover:text-primary'
                        >
                            {PHONE_ICON}
                            <span className='text-sm font-medium whitespace-nowrap'>{PHONE_NUMBER}</span>
                        </a>
                        <a
                            href="mailto:contact@liguee1.com"
                            aria-label={c.emailLabel}
                            className='w-10 h-10 flex items-center justify-center rounded-full border border-line text-ink-muted transition-all duration-300 hover:border-primary hover:text-primary'
                        >
                            {MAIL_ICON}
                        </a>
                        <a href="https://wa.me/32460201577" target="_blank" rel="noopener noreferrer" aria-label={c.whatsappLabel} className='w-10 h-10 flex items-center justify-center rounded-full border border-[#25d366]/30 text-[#25d366] transition-all duration-300 hover:bg-[#25d366]/10'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="19px" height="19px" viewBox="0 0 448 512">
                                <path fill="currentColor" d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222c0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222c0-59.3-25.2-115-67.1-157m-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4l-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2c0-101.7 82.8-184.5 184.6-184.5c49.3 0 95.6 19.2 130.4 54.1s56.2 81.2 56.1 130.5c0 101.8-84.9 184.6-186.6 184.6m101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18c-5.1-1.9-8.8-2.8-12.5 2.8s-14.3 18-17.6 21.8c-3.2 3.7-6.5 4.2-12 1.4c-32.6-16.3-54-29.1-75.5-66c-5.7-9.8 5.7-9.1 16.3-30.3c1.8-3.7.9-6.9-.5-9.7s-12.5-30.1-17.1-41.2c-4.5-10.8-9.1-9.3-12.5-9.5c-3.2-.2-6.9-.2-10.6-.2s-9.7 1.4-14.8 6.9c-5.1 5.6-19.4 19-19.4 46.3s19.9 53.7 22.6 57.4c2.8 3.7 39.1 59.7 94.8 83.8c35.2 15.2 49 16.5 66.6 13.9c10.7-1.6 32.8-13.4 37.4-26.4s4.6-24.1 3.2-26.4c-1.3-2.5-5-3.9-10.5-6.6"></path>
                            </svg>
                        </a>
                        <a href="#services" className='gold-gradient text-ink px-5 py-2.5 rounded-full transition-all duration-300 shadow-sm hover:shadow-md text-sm font-bold'>
                            {c.offersCta}
                        </a>
                    </div>

                    {/* Tablet Contact - Normal Layout */}
                    <div className='hidden md:flex lg:hidden items-center justify-end gap-2'>
                        <Link
                            to={altLangPath}
                            aria-label={otherLang === 'es' ? 'Cambiar a español' : 'Passer en français'}
                            className='flex items-center justify-center w-9 h-9 rounded-full border border-line text-ink-muted text-xs font-bold uppercase transition-all duration-300 hover:border-primary hover:text-primary'
                        >
                            {otherLang}
                        </Link>
                        <a
                            href={PHONE_TEL}
                            aria-label={c.callLabel(PHONE_NUMBER)}
                            className='w-9 h-9 flex items-center justify-center rounded-full border border-line text-ink-muted transition-all duration-300 hover:border-primary hover:text-primary'
                        >
                            {PHONE_ICON}
                        </a>
                        <a
                            href="mailto:contact@liguee1.com"
                            aria-label={c.emailLabel}
                            className='w-9 h-9 flex items-center justify-center rounded-full border border-line text-ink-muted transition-all duration-300 hover:border-primary hover:text-primary'
                        >
                            {MAIL_ICON}
                        </a>
                        <a href="https://wa.me/32460201577" target="_blank" rel="noopener noreferrer" aria-label={c.whatsappLabel} className='w-9 h-9 flex items-center justify-center rounded-full border border-[#25d366]/30 text-[#25d366] transition-all duration-300 hover:bg-[#25d366]/10'>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17px" height="17px" viewBox="0 0 448 512">
                                <path fill="currentColor" d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222c0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222c0-59.3-25.2-115-67.1-157m-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4l-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2c0-101.7 82.8-184.5 184.6-184.5c49.3 0 95.6 19.2 130.4 54.1s56.2 81.2 56.1 130.5c0 101.8-84.9 184.6-186.6 184.6m101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18c-5.1-1.9-8.8-2.8-12.5 2.8s-14.3 18-17.6 21.8c-3.2 3.7-6.5 4.2-12 1.4c-32.6-16.3-54-29.1-75.5-66c-5.7-9.8 5.7-9.1 16.3-30.3c1.8-3.7.9-6.9-.5-9.7s-12.5-30.1-17.1-41.2c-4.5-10.8-9.1-9.3-12.5-9.5c-3.2-.2-6.9-.2-10.6-.2s-9.7 1.4-14.8 6.9c-5.1 5.6-19.4 19-19.4 46.3s19.9 53.7 22.6 57.4c2.8 3.7 39.1 59.7 94.8 83.8c35.2 15.2 49 16.5 66.6 13.9c10.7-1.6 32.8-13.4 37.4-26.4s4.6-24.1 3.2-26.4c-1.3-2.5-5-3.9-10.5-6.6"></path>
                            </svg>
                        </a>
                        <a href="#services" className='gold-gradient text-ink px-4 py-2 rounded-full transition-all duration-300 shadow-sm text-sm font-bold'>
                            {c.offersCta}
                        </a>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        type="button"
                        aria-label={menuOpen ? c.closeMenu : c.openMenu}
                        aria-expanded={menuOpen}
                        aria-controls="mobile-menu"
                        onClick={() => setMenuOpen((open) => !open)}
                        className='md:hidden relative w-10 h-10 flex flex-col items-center justify-center gap-[5px] rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary'
                    >
                        <span className={`block w-5 h-[2px] bg-ink rounded-full transition-all duration-300 ${menuOpen ? 'translate-y-[7px] rotate-45' : ''}`}></span>
                        <span className={`block w-5 h-[2px] bg-ink rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                        <span className={`block w-5 h-[2px] bg-ink rounded-full transition-all duration-300 ${menuOpen ? '-translate-y-[7px] -rotate-45' : ''}`}></span>
                    </button>
                </div>
            </header>

            {/* Mobile menu — fullscreen takeover */}
            <div
                id="mobile-menu"
                className={`md:hidden fixed inset-0 z-[998] bg-surface transition-all duration-500 ${menuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                role="dialog"
                aria-modal="true"
            >
                <div className="absolute inset-0 ambient-glow"></div>
                <div className="relative h-full flex flex-col">
                    <div className='flex items-center justify-between p-5 border-b border-line'>
                        <img src={logo} alt="logo" className='w-9 h-9' />
                        <button
                            type="button"
                            aria-label={c.closeMenu}
                            onClick={() => setMenuOpen(false)}
                            className='w-10 h-10 flex items-center justify-center rounded-full border border-line hover:border-primary hover:text-primary transition-colors'
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <nav className='flex flex-col justify-center flex-1 px-8 gap-2'>
                        {c.navLinks.map((link, i) => (
                            <a
                                key={link.href}
                                href={link.href}
                                onClick={() => setMenuOpen(false)}
                                style={{ transitionDelay: menuOpen ? `${i * 60}ms` : '0ms' }}
                                className={`font-extrabold uppercase tracking-tight text-ink py-3 border-b border-line hover:text-primary transition-all duration-500 ${menuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
                            >
                                <span style={{ fontSize: 'clamp(1.75rem, 8vw, 2.5rem)' }}>{link.label}</span>
                            </a>
                        ))}
                    </nav>
                    <div className='p-8 flex flex-col gap-3'>
                        <Link
                            to={altLangPath}
                            onClick={() => setMenuOpen(false)}
                            className='flex items-center justify-center gap-2 text-ink-muted font-semibold py-3 rounded-full border border-line uppercase text-sm tracking-wide'
                        >
                            {otherLang === 'es' ? 'Cambiar a español' : 'Passer en français'}
                        </Link>
                        <a
                            href={PHONE_TEL}
                            className='flex items-center justify-center gap-2 text-ink font-semibold py-3 rounded-full border border-line'
                        >
                            {PHONE_ICON}
                            <span>{PHONE_NUMBER}</span>
                        </a>
                        <div className='flex items-center gap-3'>
                            <a
                                href="mailto:contact@liguee1.com"
                                aria-label={c.emailLabel}
                                className='flex-1 flex items-center justify-center gap-2 text-ink-muted font-medium py-3 rounded-full border border-line'
                            >
                                {MAIL_ICON}
                                <span>{c.emailShort}</span>
                            </a>
                            <a
                                href="https://wa.me/32460201577"
                                target="_blank"
                                rel="noopener noreferrer"
                                className='flex-1 flex items-center justify-center gap-2 text-[#25d366] font-medium py-3 rounded-full border border-[#25d366]/30'
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 448 512">
                                    <path fill="#25d366" d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222c0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222c0-59.3-25.2-115-67.1-157m-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4l-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2c0-101.7 82.8-184.5 184.6-184.5c49.3 0 95.6 19.2 130.4 54.1s56.2 81.2 56.1 130.5c0 101.8-84.9 184.6-186.6 184.6m101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18c-5.1-1.9-8.8-2.8-12.5 2.8s-14.3 18-17.6 21.8c-3.2 3.7-6.5 4.2-12 1.4c-32.6-16.3-54-29.1-75.5-66c-5.7-9.8 5.7-9.1 16.3-30.3c1.8-3.7.9-6.9-.5-9.7s-12.5-30.1-17.1-41.2c-4.5-10.8-9.1-9.3-12.5-9.5c-3.2-.2-6.9-.2-10.6-.2s-9.7 1.4-14.8 6.9c-5.1 5.6-19.4 19-19.4 46.3s19.9 53.7 22.6 57.4c2.8 3.7 39.1 59.7 94.8 83.8c35.2 15.2 49 16.5 66.6 13.9c10.7-1.6 32.8-13.4 37.4-26.4s4.6-24.1 3.2-26.4c-1.3-2.5-5-3.9-10.5-6.6"></path>
                                </svg>
                                <span>{c.whatsappShort}</span>
                            </a>
                        </div>
                        <a
                            href="#services"
                            onClick={() => setMenuOpen(false)}
                            className='btn-glow btn-glow-gold gold-gradient flex items-center justify-center gap-2 text-ink font-bold py-3.5 rounded-full shadow-sm'
                        >
                            <span>{c.offersCta}</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Floating WhatsApp Button */}
            <div className="fixed bottom-6 right-6 z-50">
                <div className="relative">
                    {/* Concentric circles for pulsing effect */}
                    <div className="absolute inset-0 bg-[#25d366] rounded-full animate-ping opacity-20"></div>
                    <div className="absolute inset-0 bg-[#25d366] rounded-full animate-ping opacity-15" style={{animationDelay: '0.5s'}}></div>
                    <div className="absolute inset-0 bg-[#25d366] rounded-full animate-ping opacity-10" style={{animationDelay: '1s'}}></div>

                    {/* Main WhatsApp button */}
                    <a
                        href="https://wa.me/32460201577"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative bg-[#25d366] hover:bg-[#128C7E] text-white w-14 h-14 md:w-20 md:h-20 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border-4 border-white"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 448 512">
                            <path fill="currentColor" d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222c0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222c0-59.3-25.2-115-67.1-157m-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4l-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2c0-101.7 82.8-184.5 184.6-184.5c49.3 0 95.6 19.2 130.4 54.1s56.2 81.2 56.1 130.5c0 101.8-84.9 184.6-186.6 184.6m101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18c-5.1-1.9-8.8-2.8-12.5 2.8s-14.3 18-17.6 21.8c-3.2 3.7-6.5 4.2-12 1.4c-32.6-16.3-54-29.1-75.5-66c-5.7-9.8 5.7-9.1 16.3-30.3c1.8-3.7.9-6.9-.5-9.7s-12.5-30.1-17.1-41.2c-4.5-10.8-9.1-9.3-12.5-9.5c-3.2-.2-6.9-.2-10.6-.2s-9.7 1.4-14.8 6.9c-5.1 5.6-19.4 19-19.4 46.3s19.9 53.7 22.6 57.4c2.8 3.7 39.1 59.7 94.8 83.8c35.2 15.2 49 16.5 66.6 13.9c10.7-1.6 32.8-13.4 37.4-26.4s4.6-24.1 3.2-26.4c-1.3-2.5-5-3.9-10.5-6.6"></path>
                        </svg>
                    </a>
                </div>
            </div>
        </>
    )
}
